import PrintFooter from "./PrintFooter";
import { Alert, Avatar, Button, Space } from "antd";
import React, { useState } from "react";
import { PrintDTO } from "../../types/print/PrintDTO";
import { useDeletePrintMutation } from "../../store/api/printApi";
import DeleteButton from "../../components/buttons/DeleteButton";
import { createUseStyles } from "react-jss";
import { DeleteParams } from "../../types/utils/Constants";
import { MutationActionCreatorResult } from "@reduxjs/toolkit/dist/query/core/buildInitiate";
import ReprintButton from "../../components/buttons/ReprintButton";

const useStyles = createUseStyles<string, { isActive: boolean }>(() => ({
  alert: ({ isActive }) => ({
    margin: "5px",
    boxShadow: isActive && "8px 8px 12px 0 rgba(255, 99, 71, 0.4)",
    "&:hover": {
      boxShadow: "8px 8px 12px 0 rgba(255, 99, 71, 0.4)",
    },
    transition: ".2s ease box-shadow",
  }),
  deleted: ({ isActive }) => ({
    position: "relative",
    width: "max-content",
    overflow: "hidden",
    opacity: isActive ? 0.6 : 1,
    "&:after": {
      content: '""',
      position: "absolute",
      top: "50%",
      left: isActive ? 0 : "-100%",
      width: "100%",
      height: "2px",
      backgroundColor: "#757575",
      display: "block",
      transition: ".2s ease-in left",
    },
  }),
}));

type PrintCardProps = {
  item: PrintDTO;
  checkedItems?: number[];
  onClickAction?: (id: number, isInsertion: boolean) => void;
  onDoubleClickAction?: (print: PrintDTO) => void;
};

const PrintCard: React.FC<PrintCardProps> = ({
  item,
  onClickAction,
  onDoubleClickAction,
  checkedItems,
}) => {
  const [deletePrint, { isLoading: deletePrintIsLoading }] =
    useDeletePrintMutation();
  const [isActive, setActive] = useState<boolean>(false);

  const classes = useStyles({ isActive });

  const alertHeader = (
    <p className={classes.deleted}>{`${item.name.ru} ${
      item.measurement?.measurement_number ?? ""
    } ${item.measurement?.type.name.ru ?? ""}`}</p>
  );

  const onBlockClicked = (e: React.MouseEvent) => {
    if (e.detail === 1) {
      // CLICK
      setActive((p) => !p);
      onClickAction?.call(null, item.id, !isActive);
    } else if (e.detail === 2) {
      // DOUBLE CLICK
      setActive((p) => !p);
      onClickAction?.call(null, item.id, !isActive);
      onDoubleClickAction?.call(null, item);
    }
  };

  const deleteItem = (
    params: DeleteParams
  ): MutationActionCreatorResult<any> => {
    params.id =
      checkedItems &&
      checkedItems.length !== 0 &&
      checkedItems.includes(item.id)
        ? checkedItems.toString()
        : params.id;
    checkedItems?.splice(0);
    return deletePrint(params);
  };

  return (
    <Alert
      className={classes.alert}
      message={alertHeader}
      description={
        <PrintFooter
          styleAsDeleted={isActive}
          producer={item.producer}
          expiration_date={item.expiration_date}
        />
      }
      action={
        <Space direction={"vertical"}>
          <DeleteButton
            id={item.id}
            deleteFunc={deleteItem}
            isLoading={deletePrintIsLoading}
            hasConfirmation={false}
            size={"large"}
          />
          <ReprintButton
            clickAction={() => onDoubleClickAction?.call(null, item)}
          />
        </Space>
      }
      icon={<Avatar src={item.picture_url} />}
      type={item.type}
      showIcon
      onClick={onBlockClicked}
    />
  );
};

export default PrintCard;
